import React, { useEffect, useRef, useState } from "react";
import { _ProductDetail } from "./Products";
import { Link } from "react-router-dom";
import { saveListReturnState } from "./navMemory";
import { isInWishlist, toggleWishlist } from "./wishlist";
import { renderDifficultyBadge } from "./difficulty";


interface ProductBoxProps {
    item: _ProductDetail[];
    productID: number;
    handleProductItem: (slug: string) => void;
}

const checkDifficulty = (difficulty: number) => renderDifficultyBadge(difficulty);

interface RotatingImageProps {
    item: any;
    onClick: () => void;
}

const RotatingImage: React.FC<RotatingImageProps> = ({ item, onClick }) => {
    const placeholder = "/img/img_placeholder.jpg";
    const gallery: string[] = (item.gallery && item.gallery.length > 0)
        ? item.gallery
        : (item.thumbnail ? [item.thumbnail] : [placeholder]);

    const [idx, setIdx] = useState(0);
    const [errored, setErrored] = useState(false);
    const intervalRef = useRef<number | null>(null);

    const stopRotation = () => {
        if (intervalRef.current !== null) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const startRotation = () => {
        if (gallery.length <= 1) return;
        stopRotation();
        intervalRef.current = window.setInterval(() => {
            setIdx((i) => (i + 1) % gallery.length);
        }, 900);
    };

    useEffect(() => stopRotation, []);

    const onEnter = () => startRotation();
    const onLeave = () => { stopRotation(); setIdx(0); };

    return (
        <Link
            to={`/product/${item.slug}`}
            className="img openDetail pd-list-img"
            onClick={onClick}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            {gallery.map((src, i) => (
                <img
                    key={src}
                    src={errored ? placeholder : src}
                    alt={item.name}
                    onError={() => setErrored(true)}
                    loading="lazy"
                    width="384"
                    height="512"
                    className={`pd-list-img__layer${i === idx ? ' is-active' : ''}`}
                    aria-hidden={i !== idx}
                />
            ))}
            {checkDifficulty(item.difficulty)}
            {gallery.length > 1 && (
                <div className="pd-list-img__dots" aria-hidden="true">
                    {gallery.map((_, i) => (
                        <span key={i} className={`pd-list-img__dot${i === idx ? ' is-active' : ''}`} />
                    ))}
                </div>
            )}
        </Link>
    );
};

const HeartToggle: React.FC<{ productId: number; productTitle: string }> = ({ productId, productTitle }) => {
    const [active, setActive] = useState<boolean>(() => isInWishlist(productId));
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        const onChange = () => setActive(isInWishlist(productId));
        window.addEventListener('wishlist-changed', onChange);
        return () => window.removeEventListener('wishlist-changed', onChange);
    }, [productId]);

    const onClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const next = toggleWishlist(productId);
        setActive(next);
        setAnimate(true);
        setTimeout(() => setAnimate(false), 400);
    };

    return (
        <button
            type="button"
            className={`pd-card-heart${active ? ' is-active' : ''}${animate ? ' is-animating' : ''}`}
            onClick={onClick}
            aria-label={active ? `Odebrat „${productTitle}" z oblíbených` : `Přidat „${productTitle}" do oblíbených`}
            title={active ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'}
        >
            <span aria-hidden="true">{active ? '♥' : '♡'}</span>
        </button>
    );
};

const ProductBox: React.FC<ProductBoxProps> = ({ item, productID, handleProductItem }) => {
    const onProductClick = () => saveListReturnState();

    return (
        <>
            {item.map((Val: _ProductDetail) => (
                <div key={Val.id}>
                    <div className="product" style={{ position: 'relative' }}>
                        <HeartToggle productId={Val.id} productTitle={Val.name} />
                        <RotatingImage item={Val} onClick={onProductClick} />

                        <div className="desc">
                            <Link to={`/product/${Val.slug}`} className="openDetail" onClick={onProductClick}>
                                {Val.name}
                            </Link>
                            <span className="amount">{Val.price} Kč</span>
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
};

export default ProductBox;
